<template>
  <div
    :class="[$style.base, { [$style.smallScreen]: $screen === 's'}]"
    v-component="'sp-pricing'"
  >
    <div :class="$style.header">
      <BaseHeading center>
        {{ $t('register.salonized_pay.landing.pricing_section.heading') }}
      </BaseHeading>
      <BaseSwitch
        v-model="activeTab"
        :options="tabOptions"
        :class="$style.switch"
        v-test="'sp-pricing-tab-switch'"
      />
    </div>
    <div :class="$style.pricing">
      <div v-if="activeTab === 'in-store'">
        <div
          v-for="option in offlinePaymentOptions"
          :key="option.title"
          :class="$style.line"
          v-test="'sp-pricing-offline'"
        >
          <BaseText color="inverted">{{ option.title }}</BaseText>
          <BaseText
            color="inverted"
            bold
          >
            {{ option.description }}
          </BaseText>
        </div>
      </div>
      <div v-if="activeTab === 'online'">
        <div
          v-for="option in onlinePaymentOptions"
          :key="option.title"
          :class="$style.line"
          v-test="'sp-pricing-online'"
        >
          <BaseText color="inverted">{{ option.title }}</BaseText>
          <BaseText
            color="inverted"
            bold
          >
            {{ option.description }}
          </BaseText>
        </div>
      </div>
    </div>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import { useCompanyStore } from '@/stores/company';
import { useTerminalData } from './terminals';

const { terminals } = useTerminalData();

const ams1Terminal = computed(() =>
  terminals.value.find((t) => t.key === 'AMS1'),
);

const { company, isUk } = useCompanyStore();
const isSwitzerland = company.country === 'ch';
const isFrance = company.country === 'fr';

const { t } = useI18n();

const activeTab = ref('in-store');

const tabOptions = ref([
  {
    label: t(
      'register.salonized_pay.landing.pricing_section.tab_instore_label',
    ),
    value: 'in-store',
  },
  {
    label: t('register.salonized_pay.landing.pricing_section.tab_online_label'),
    value: 'online',
  },
]);

const offlinePaymentOptions = computed(() => [
  ...(isUk
    ? [
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.cards_domestic',
          ),
          description: '1%',
        },
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.cards_premium',
          ),
          description: '2.5%',
        },
      ]
    : isSwitzerland
      ? [
          { title: 'Twint', description: '1.5%' },
          {
            title: t(
              'register.salonized_pay.landing.pricing_section.cards_domestic',
            ),
            description: '1.29%',
          },
          {
            title: t(
              'register.salonized_pay.landing.pricing_section.cards_premium',
            ),
            description: '1.99%',
          },
          { title: 'American Express (AMEX)', description: '3.25%' },
        ]
      : isFrance
        ? [
            {
              title: t(
                'register.salonized_pay.landing.pricing_section.cards_domestic',
              ),
              description: '1.29%',
            },
            {
              title: t(
                'register.salonized_pay.landing.pricing_section.cards_premium',
              ),
              description: '1.99%',
            },
            { title: 'American Express (AMEX)', description: '3.49%' },
          ]
        : [
            {
              title: t(
                'register.salonized_pay.landing.pricing_section.title_1',
              ),
              description: t(
                'register.salonized_pay.landing.pricing_section.description_1',
              ),
            },
          ]),
  {
    title: t('register.salonized_pay.landing.pricing_section.title_2'),
    description: t(
      'register.salonized_pay.landing.pricing_section.description_2_variable',
      {
        price: filters.currency(
          ams1Terminal.value?.price ?? 0,
          ams1Terminal.value?.currency,
        ),
      },
    ),
  },
  {
    title: t('register.salonized_pay.landing.pricing_section.title_3'),
    description: filters.currency(0, ams1Terminal.value?.currency),
  },
]);

const onlinePaymentOptionsSwitzerland = computed(() => [
  { title: 'Twint', description: '1.5%' },
  {
    title: t('register.salonized_pay.landing.pricing_section.cards_domestic'),
    description: `1.49% + ${filters.currency(20, ams1Terminal.value?.currency)}`,
  },
  {
    title: t('register.salonized_pay.landing.pricing_section.cards_premium'),
    description: `2.50% + ${filters.currency(20, ams1Terminal.value?.currency)}`,
  },
  { title: 'American Express (AMEX)', description: '3.25%' },
]);

const onlinePaymentOptionsFrance = computed(() => [
  {
    title: t('register.salonized_pay.landing.pricing_section.cards_domestic'),
    description: `1.39% + ${filters.currency(20, ams1Terminal.value?.currency)}`,
  },
  {
    title: t('register.salonized_pay.landing.pricing_section.cards_premium'),
    description: `2.50% + ${filters.currency(20, ams1Terminal.value?.currency)}`,
  },
  { title: 'American Express (AMEX)', description: '3.49%' },
]);

const onlinePaymentOptionsDefault = computed(() => [
  {
    title: t(
      'register.salonized_pay.landing.pricing_section.online_payments.title_1',
    ),
    description: t(
      'register.salonized_pay.landing.pricing_section.online_payments.description_1',
    ),
  },
  ...(isUk
    ? [
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.cards_domestic',
          ),
          description: `1.45% + ${filters.currency(20, ams1Terminal.value?.currency)}`,
        },
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.cards_premium',
          ),
          description: `2.80% + ${filters.currency(20, ams1Terminal.value?.currency)}`,
        },
      ]
    : [
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.online_payments.title_2',
          ),
          description: t(
            'register.salonized_pay.landing.pricing_section.online_payments.description_2',
          ),
        },
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.online_payments.title_3',
          ),
          description: t(
            'register.salonized_pay.landing.pricing_section.online_payments.description_3',
          ),
        },
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.online_payments.title_4',
          ),
          description: t(
            'register.salonized_pay.landing.pricing_section.online_payments.description_4',
          ),
        },
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.online_payments.title_5',
          ),
          description: t(
            'register.salonized_pay.landing.pricing_section.online_payments.description_5',
          ),
        },
        {
          title: t(
            'register.salonized_pay.landing.pricing_section.online_payments.title_6',
          ),
          description: t(
            'register.salonized_pay.landing.pricing_section.online_payments.description_6',
          ),
        },
      ]),
]);

const onlinePaymentOptions = computed(() => {
  if (isSwitzerland) {
    return onlinePaymentOptionsSwitzerland.value;
  }

  if (isFrance) {
    return onlinePaymentOptionsFrance.value;
  }

  return onlinePaymentOptionsDefault.value;
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  width: 950px;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: $spacing;
  gap: $spacing;

  .base.smallScreen & {
    flex-direction: column;
    align-items: center;
  }
}

.pricing {
  background-size: 100% 100%;
  background-position:
    0px 0px,
    0px 0px,
    0px 0px,
    0px 0px;
  background-image: radial-gradient(
      70% 300% at 5% 104%,
      #c9c8ee5a 1%,
      #073aff00 30%
    ),
    radial-gradient(30% 180% at 35% 12%, #03cfa644 0.1%, #073aff00 30%),
    radial-gradient(20% 100% at 105% 51%, #c9c8ee70 1%, #073aff00 30%),
    linear-gradient(136deg, #262563 24.66%, #6d73db 98.66%);
  border-radius: $radius;
  padding: $spacing;
  width: 100%;
}

.switchContainer {
  width: 100%;
  margin-bottom: $spacing * 2;
}

.line {
  display: flex;
  justify-content: space-between;
  border: none;
  padding: $spacing 0px $spacing 0px;
  &:not(:last-of-type) {
    border-bottom: 1px solid var(--color-primary-light);
  }
}
</style>
